import { motion, useMotionValue, useTransform } from "framer-motion";

function InteractiveCard({ title, description, delay = 0 }) {
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);

  const rotateX = useTransform(pointerY, [0, 1], [8, -8]);
  const rotateY = useTransform(pointerX, [0, 1], [-8, 8]);

  const handleMouseMove = (event) => {
    const boundingRect = event.currentTarget.getBoundingClientRect();
    const relativeX = (event.clientX - boundingRect.left) / boundingRect.width;
    const relativeY = (event.clientY - boundingRect.top) / boundingRect.height;
    pointerX.set(relativeX);
    pointerY.set(relativeY);
  };

  const handleMouseLeave = () => {
    pointerX.set(0.5);
    pointerY.set(0.5);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay }}
      className="relative"
    >
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY }}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="group relative overflow-hidden p-6 rounded-2xl border border-emerald-900/10 bg-gradient-to-br from-white to-emerald-50/40 shadow-[0_6px_30px_-12px_rgba(16,185,129,0.25)]"
      >
        {/* Soft corner glows */}
        <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full blur-3xl pointer-events-none bg-emerald-300/20" />
        <div className="absolute -right-24 -bottom-24 w-64 h-64 rounded-full blur-3xl pointer-events-none bg-emerald-200/10" />

        {/* Shine sweep */}
        <div className="absolute inset-0 opacity-0 transition-opacity duration-500 pointer-events-none group-hover:opacity-100">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-transparent transition-transform duration-700 -translate-x-full group-hover:translate-x-0 via-white/20" />
        </div>

        <div className="relative z-10">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-gray-600">{description}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function WhoWeAre() {
  return (
    <section className="relative py-14 bg-gradient-to-b from-white via-white md:py-18 to-emerald-50/30">
      <div className="px-6 mx-auto max-w-6xl">
        <div className="text-center">
          <div className="inline-block relative">
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 md:text-3xl">
              About TripSecure
            </h2>
            <span className="absolute -bottom-1 left-1/2 w-11 h-2 rounded-full -translate-x-1/2 bg-emerald-300/80" />
          </div>
          <p className="mt-3 text-sm text-gray-600 md:text-base">
            We are Africa's first all-in-one travel safety and protection ecosystem, built to make every journey safer, simpler, and smarter.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 mt-8 md:mt-12 md:grid-cols-2">
          <InteractiveCard
            title="Our Vision"
            description="To become Africa's most trusted travel safety brand, enabling every traveller to explore freely, knowing they are protected wherever they go."
            delay={0.1}
          />
          <InteractiveCard
            title="Our Mission"
            description="To provide accessible, affordable, and technology-driven travel protection and safety services for individuals, groups, and organisations — through innovation, partnerships, and trust."
            delay={0.2}
          />
        </div>

        {/* Core Divisions Section */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Our Core Divisions</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              TripSecure operates across four key divisions to provide comprehensive travel safety and protection services.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <InteractiveCard
              title="PAYG Travel Insurance"
              description="We pioneered Africa's Pay-As-You-Go travel insurance, offering flexible coverage that fits your journey — whether it's a short domestic trip or an international adventure."
              delay={0.1}
            />
            <InteractiveCard
              title="Travel, Tours & Smart Logistics"
              description="We plan, manage, and move with precision. From group tours and pilgrimages to flight bookings and courier services, TripSecure provides safe, transparent, and insured movement solutions."
              delay={0.2}
            />
            <InteractiveCard
              title="Safety Services"
              description="Your personal safety companion on the road, in the air, or at your destination. Verified airport pickups, roadside assistance, and 24/7 safety helpline for travellers and logistics clients."
              delay={0.3}
            />
            <InteractiveCard
              title="Media & Events"
              description="We tell Africa's travel and safety stories through creative media, campaigns, and experiential events that promote safety awareness and travel culture."
              delay={0.4}
            />
          </div>
        </div>

        {/* Why Choose TripSecure Section */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Why Choose TripSecure?</h3>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100">
              <h4 className="font-semibold text-gray-900 mb-2">All-in-One Protection</h4>
              <p className="text-sm text-gray-600">Insurance, safety, and logistics under one brand</p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-blue-50 to-white border border-blue-100">
              <h4 className="font-semibold text-gray-900 mb-2">Flexible Payment Options</h4>
              <p className="text-sm text-gray-600">Including instalment plans for tours</p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-purple-50 to-white border border-purple-100">
              <h4 className="font-semibold text-gray-900 mb-2">Verified Partners</h4>
              <p className="text-sm text-gray-600">Vetted drivers, couriers, and tour operators</p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-orange-50 to-white border border-orange-100">
              <h4 className="font-semibold text-gray-900 mb-2">24/7 Support</h4>
              <p className="text-sm text-gray-600">Emergency and customer assistance round the clock</p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-green-50 to-white border border-green-100">
              <h4 className="font-semibold text-gray-900 mb-2">African-Focused Innovation</h4>
              <p className="text-sm text-gray-600">Built to meet the needs of African travellers</p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100">
              <h4 className="font-semibold text-gray-900 mb-2">Our Commitment</h4>
              <p className="text-sm text-gray-600">Safety should never be an afterthought — it should travel with you</p>
            </div>
          </div>
        </div>

        {/* Our Story Section */}
        <div className="mt-16">
          <div className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl p-8 text-white">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">Our Story</h3>
              <p className="text-lg opacity-90 max-w-3xl mx-auto">
                Founded in Ghana, TripSecure began with a simple mission: to protect travellers who move every day — from short city commutes to international journeys.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold mb-3">The Challenge</h4>
                <p className="text-sm opacity-90">
                  We saw the gap between travel convenience and safety assurance — and we filled it.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-3">Today</h4>
                <p className="text-sm opacity-90">
                  TripSecure is Africa's travel safety brand with expanding operations, innovative digital tools, and a bold vision to make insurance and security accessible to everyone.
                </p>
              </div>
            </div>

            <div className="text-center mt-8">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 rounded-full">
                <span className="text-sm font-medium">Our Motto: "Securing the Future of Travel in Africa."</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


